'use client'

import { Button } from '@/components/ui/button'
import { Maximize2, Minimize2 } from 'lucide-react'
import { useContentLayout } from '@/lib/contexts/content-layout-context'
import { usePageHeader } from '@/lib/contexts/page-header-context'

/**
 * PageTopBar Component
 *
 * A permanent top bar that displays page-specific content:
 * - Page title and subtitle (from context)
 * - Action buttons (from context)
 * - Expand/collapse content toggle
 *
 * This component is part of the layout and always visible.
 * Pages use `useSetPageHeader` hook to set their content.
 */
export function PageTopBar() {
  const { isExpanded, toggleExpanded } = useContentLayout()
  const { title, subtitle, actions } = usePageHeader()

  // Don't render if no title is set
  if (!title) {
    return null
  }

  return (
    <div className="sticky top-0 z-10 border-b border-sidebar-border shadow-md">
      {/* Gradient background matching sidebar theme */}
      <div className="bg-gradient-to-r from-sidebar/98 via-sidebar to-sidebar/95 dark:from-sidebar/99 dark:via-sidebar dark:to-sidebar/97 shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
        <div className="flex h-16 items-center justify-between px-6">
          {/* Left Section: Title and Subtitle Stacked */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <h1 className="text-lg font-semibold tracking-tight text-foreground truncate leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-xs text-muted-foreground truncate leading-tight mt-0.5">
                {subtitle}
              </p>
            )}
          </div>

          {/* Right Section: Actions and Toggle */}
          <div className="flex items-center space-x-3 ml-4 shrink-0">
            {/* Dynamic Action Buttons */}
            {actions && (
              <div className="flex items-center space-x-2">
                {actions}
              </div>
            )}

            {/* Expand/Collapse Toggle */}
            <Button
              variant="outline"
              size="icon"
              onClick={toggleExpanded}
              title={isExpanded ? 'Collapse content' : 'Expand content'}
              className="shrink-0"
            >
              {isExpanded ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
